// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify, { NotifyProps } from "@eisinc/App/Components/Notify";
import { shipmentDateTypeValues } from "@eisinc/Overrides/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitCarrierService";
import { parseBoolean, xClassNames } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCurrentCartState,
    hasQuoteRequiredProducts,
    hasRestrictedCartLines,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    return {
        cart,
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        hasRestrictedCartLines: hasRestrictedCartLines(cart),
        isCartPage: getCurrentPage(state)?.type === "CartPage",
    };
};

export interface CartApproverMessageStyles {
    approvingText?: TypographyPresentationProps;
    approvingWrapper?: InjectableCss;
    // XNG-Changes
    notify?: NotifyProps;
    message?: TypographyPresentationProps;
}

export const cartApproverMessageStyles: CartApproverMessageStyles = {
    approvingWrapper: {
        css: css`
            text-align: center;
            padding: 10px;
            background-color: ${getColor("info.main")};
        `,
    },
    approvingText: {
        css: css`
            width: 100%;
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const styles = cartApproverMessageStyles;
const CartApproverMessage = ({ cart, hasQuoteRequiredProducts, hasRestrictedCartLines, isCartPage }: Props) => {
    const [outOfStock, setOutOfStock] = React.useState(false);

    if (!cart || cart.cartLines?.length === 0) {
        return null;
    }

    let cartNotPriced = false;
    let belowOrderThreshold = false;
    let inactiveProducts = false;
    let customQuote = false;
    let canModifyCuts = false;
    let cutsDoesNotEqualQty = false;
    const extendedCutTotal: boolean[] = [];

    if (cart && cart.cartLines?.length) {
        customQuote = cart.cartLines.some(line => line.pricing?.unitNetPrice === 0);
        inactiveProducts = cart.cartLines.some(line => !line.isActive);
        belowOrderThreshold = parseBoolean(cart.properties["orderLessThanMinimumOrderValue"]);
        cartNotPriced = cart?.cartNotPriced;
        canModifyCuts = cart?.cartLines.some(
            line =>
                line.properties["allowCutWire"] === "true" &&
                (line.properties["cutWire"] === undefined || line.properties["cutWire"] === ""),
        );
        // eslint-disable-next-line array-callback-return
        cart?.cartLines.map(line => {
            if (
                line.properties["allowCutWire"] === "true" &&
                // eslint-disable-next-line no-prototype-builtins
                line.properties?.hasOwnProperty("cutWire") &&
                line.properties["cutWire"] !== ""
            ) {
                const cutObj = JSON.parse(line.properties["cutWire"]);
                const newExtendedTotal = cutObj.reduce(function (prev: any, current: any) {
                    return prev + parseInt(current.total, 10);
                }, 0);
                extendedCutTotal.push(line.qtyOrdered !== newExtendedTotal);
            }
        });
        cutsDoesNotEqualQty = extendedCutTotal.some(o => o);
    }

    if (cart && cart.cartLines) {
        const isOutStock = cart.cartLines?.some(line => line.availability!.messageType === 2);
        if (isOutStock !== outOfStock) {
            setOutOfStock(isOutStock);
        }
    }

    // Xng-change: EIS-3697: adding a check when checkout is disabled for quickFab products
    const isCheckoutDisabled =
        parseBoolean(cart.properties.disablePickupMethod) && cart.fulfillmentMethod === FulfillmentMethod.PickUp;

    return (
        <StyledWrapper
            {...styles.approvingWrapper}
            data-test-selector="CartApproverMessage"
            className={xClassNames.append(isCartPage && "hide-line")}
        >
            {cart.isAwaitingApproval && (
                <Notify
                    {...styles.notify}
                    messageType="info"
                    body={translate(
                        "You are approving order #{0} for {1}",
                        cart.orderNumber,
                        cart.initiatedByUserName?.toUpperCase(),
                    )}
                    data-test-selector="cart_approvingOrderMessage"
                />
            )}
            {/* XNG-Change */}
            {isCartPage &&
                (parseBoolean(cart?.properties?.hasScheduledDeliveries) ||
                    cart?.properties?.shipmentDateType === shipmentDateTypeValues.OneDate) && (
                    <Notify
                        {...styles.notify}
                        messageType="warning"
                        body={siteMessage("Cart_Scheduled_Order_Notification")}
                    />
                )}
            {(hasQuoteRequiredProducts || customQuote) && isCartPage && (
                <Notify
                    {...styles.notify}
                    messageType="warning"
                    body={siteMessage("Custom_Cart_has_quote_required_products")}
                />
            )}
            {hasRestrictedCartLines && (
                <Notify {...styles.notify} messageType="warning" body={siteMessage("Cart_ProductsCannotBePurchased")} />
            )}
            {inactiveProducts && (
                <Notify {...styles.notify} messageType="warning" body={siteMessage("Cart_ProductsCannotBePurchased")} />
            )}
            {belowOrderThreshold && (
                <Notify {...styles.notify} messageType="warning" body={siteMessage("Cart_LessThanOrderThreshold")} />
            )}
            {cartNotPriced && (
                <Notify {...styles.notify} messageType="warning" body={siteMessage("Cart_PriceNotAvailable")} />
            )}
            {cart.fulfillmentMethod === FulfillmentMethod.PickUp && outOfStock && (
                <Notify {...styles.notify} messageType="warning" body={siteMessage("Custom_Cart_out_of_stock_items")} />
            )}
            {canModifyCuts && (
                <Notify {...styles.notify} messageType="info" body={siteMessage("Custom_Cart_CanModifyCuts")} />
            )}
            {cutsDoesNotEqualQty && (
                <Notify
                    {...styles.notify}
                    messageType="warning"
                    body={siteMessage("Custom_Cart_CutLengthsDoesNotMeetQtyOrdered")}
                />
            )}
            {isCheckoutDisabled && (
                <Notify
                    {...styles.notify}
                    messageType="warning"
                    body={siteMessage("Custom_Cart_Quickfab_Checkout_Fulfillment_Method")}
                />
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CartApproverMessage),
    definition: {
        displayName: "Cart Approver Message",
        icon: "cart-circle-check",
        group: "Common",
    },
};

export default widgetModule;
